import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch, withRouter, } from 'react-router';
import AllSpecifications from 'containers/Specifications/AllSpecifications';
import Filters from 'containers/Specifications/Filters/Filters';
import FolderView from 'containers/Specifications/FolderView';
import FileIcon from 'images/v5/icons/file-04.svg';
import Breadcrumbs from 'layout/atoms/Breadcrumbs/Breadcrumbs';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import Modal from 'layout/atoms/Modal/Modal';
import Grid from 'layout/molecules/Grid/Grid';
import FoldersTree from 'layout/organisms/FoldersTree/FoldersTree';
import Submenu from 'layout/organisms/Submenu/Submenu';
import Dashboard from 'layout/templates/Dashboard/Dashboard';
import { fetchRootFolder, fetchRootFolders, selectFolderName, selectFoldersTree, selectRootFolder, } from 'store/specificationsFoldersSlice';
function SpecificationsPicker({ match }) {
    var _a;
    const { t } = useTranslation(['specification', 'common', 'orders']);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const releaseMatch = useRouteMatch('/warehouse-releases/:warehouseReleaseId');
    const rootFolder = useSelector(selectRootFolder);
    const warehouseReleaseId = releaseMatch === null || releaseMatch === void 0 ? void 0 : releaseMatch.params.warehouseReleaseId;
    const { url } = match;
    const folderId = ((_a = location.pathname.match(/folders\/([^/]+)/)) === null || _a === void 0 ? void 0 : _a[1]) || (rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId);
    const folderName = useSelector((state) => selectFolderName(state, folderId));
    const foldersTree = useSelector(selectFoldersTree);
    const memoFoldersTree = useMemo(() => foldersTree, [foldersTree]);
    useEffect(() => {
        dispatch(fetchRootFolder());
    }, [dispatch]);
    useEffect(() => {
        if (rootFolder.rootFolderId !== null) {
            dispatch(fetchRootFolders({ folderId: rootFolder.rootFolderId }));
        }
    }, [dispatch, rootFolder.status, rootFolder.rootFolderId]);
    const generateBreadcrumbs = (folders, currentFolderId) => {
        const breadcrumbs = [];
        let currentFolder = folders.find((folder) => folder.id === currentFolderId);
        while (currentFolder) {
            breadcrumbs.unshift({
                id: currentFolder.id,
                to: `${match.url}/folders/${currentFolder.id}`,
                name: currentFolder.name,
            });
            // eslint-disable-next-line no-loop-func
            currentFolder = folders.find((folder) => folder.id === currentFolder.parentId);
        }
        return breadcrumbs;
    };
    const folderBreadcrumbs = memoFoldersTree ? generateBreadcrumbs(memoFoldersTree, folderId) : [];
    const handleCancel = () => history.push(`/warehouse-releases/${warehouseReleaseId}/summary`);
    const defaultLinks = [
        {
            id: 'my_files',
            to: `${match.url}/my-files`,
            text: t('specification:my_files'),
            icon: React.createElement(FileIcon, null),
        },
    ];
    const isLoading = rootFolder.status === 'idle' || rootFolder.status === 'loading';
    return (React.createElement(Modal, { name: "warehouseReleaceSpecificationPicker", size: "fullScreen", header: React.createElement(Label, { weight: "strong" }, t('common:select_specification')), noPadding: true, onClose: handleCancel },
        isLoading && React.createElement(Loader, null),
        React.createElement("div", { style: { display: isLoading ? 'none' : undefined } },
            React.createElement(Dashboard, { asideMenu: React.createElement(Grid, { vp: 12, hp: 12, gap: 0 },
                    React.createElement(Submenu, { groups: [{ id: 'defaultLinks', items: defaultLinks }] }),
                    React.createElement(FoldersTree, { rootFolderId: rootFolder.rootFolderId, folders: memoFoldersTree, link: `${match.url}/folders` })) },
                React.createElement(Grid, { vp: 12, hp: 24, gap: 0 },
                    React.createElement(Switch, null,
                        React.createElement(Route, { exact: true, path: `${match.url}/my-files` },
                            React.createElement(React.Fragment, null,
                                React.createElement(Breadcrumbs, { items: [
                                        {
                                            id: 'specifications/',
                                            to: match.url,
                                            name: t('specification_capitalize_plural'),
                                        },
                                        { id: 'my_files', to: `${match.url}/my-files`, name: t('my_files') },
                                    ] }),
                                React.createElement("h2", null, t('my_files')))),
                        React.createElement(Route, { exact: true, path: `${match.url}/folders/:folderId` },
                            React.createElement(React.Fragment, null,
                                React.createElement(Breadcrumbs, { items: [
                                        {
                                            id: '/specifications/',
                                            to: match.url,
                                            name: t('specification_capitalize_plural'),
                                        },
                                        ...folderBreadcrumbs,
                                    ] }),
                                React.createElement("h2", null, folderId === rootFolder.rootFolderId
                                    ? t('folder_capitalize_plural')
                                    : folderName))))),
                React.createElement(Switch, null,
                    React.createElement(Route, { exact: true, path: `${match.url}/my-files` },
                        React.createElement(Filters, null)),
                    React.createElement(Route, { exact: true, path: `${match.url}/folders/:folderId` },
                        React.createElement(Filters, { folders: true }))),
                React.createElement(Grid, { vp: 12, hp: 24, gap: 0 },
                    React.createElement(Switch, null,
                        React.createElement(Route, { exact: true, path: `${match.url}/my-files` },
                            React.createElement(AllSpecifications, { warehouseReleaseSelect: true })),
                        React.createElement(Route, { exact: true, path: `${match.url}/folders/:folderId` },
                            React.createElement(FolderView, { link: `${match.url}/folders`, warehouseReleaseSelect: true })),
                        React.createElement(Route, { exact: true, path: match.url }, (rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId) && (React.createElement(Redirect, { to: `${url}/folders/${rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId}` })))))))));
}
export default memo(withRouter(SpecificationsPicker));
